import React, { useState, useEffect, startTransition } from 'react';
import { Plus, Bot, Edit, Trash2, TestTube, PhoneIncoming, Loader2, Code2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Heading, Text } from '../../common/ui/typography';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  selectAgent,
  saveDefaultAgent,
  selectAgents,
  selectSelectedAgent,
} from '../../store/slices/agentsSlice';
import { selectUser } from '../../store/slices/authSlice';
import { fetchMySubscription } from '../../store/slices/subscriptionSlice';
import { usePermissions } from '../Guards/withPermissions';
import { useFeatureToggle } from '../../hooks/useFeatureToggle';
import { callMeService } from '../../services';
import AgentForm from './AgentForm';
import AgentSimpleWizard from './AgentSimpleWizard';
import AgentCreateChooser from './AgentCreateChooser';
import AgentEmbedPanel from './AgentEmbedPanel';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../common/ui/dialog';
import HelpBanner from '../Help/HelpBanner';
import UpgradePromptCard from '../Upgrade/UpgradePromptCard';
import { helpLinks } from '../../config/helpLinks';
import { useWorkspaceExperience } from '../../hooks/useWorkspaceExperience';
import { useAgencyClients } from '../../hooks/useAgencyClients';
import { useConfirm } from '../../hooks/useConfirm';

const Agents = () => {
  const confirm = useConfirm();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const agents = useAppSelector(selectAgents);
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const user = useAppSelector(selectUser);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateChooser, setShowCreateChooser] = useState(false);
  const [showSimpleWizard, setShowSimpleWizard] = useState(false);
  const [callingAgentId, setCallingAgentId] = useState(null);
  const { workspace, isAgency } = useWorkspaceExperience();
  const { activeClient } = useAgencyClients();

  // Use permission system
  const { canCreateAgent } = usePermissions();

  // Check if Call Me feature is enabled
  const isCallMeEnabled = useFeatureToggle('call_me_page', {
    level: 'page',
    defaultValue: false,
  });

  // Check if user phone is verified
  const isPhoneVerified = user?.phoneVerified || false;

  // (Removed unused provider helper mappings and handlers)

  // Load agents + subscription (plan.useRootCredentials) on mount
  useEffect(() => {
    if (agents.length === 0) {
      dispatch(fetchAgents());
    }
    dispatch(fetchMySubscription());
  }, [dispatch, agents.length]);

  // Restore default agent from user preferences if available
  useEffect(() => {
    if (!selectedAgent && agents.length > 0 && user?.preferences?.defaultAgentId) {
      const defaultAgentId = user.preferences.defaultAgentId;
      const defaultAgent = agents.find(agent => (agent._id || agent.id) === defaultAgentId);
      if (defaultAgent) {
        dispatch(selectAgent(defaultAgent));
      }
    }
  }, [agents, user?.preferences?.defaultAgentId, selectedAgent, dispatch]);
  const [editingAgent, setEditingAgent] = useState(null);
  const [embedAgent, setEmbedAgent] = useState(null);

  // (Removed unused input/provider handlers)

  // (Removed unused provider change functions)

  // (Creation handled by AgentForm via handleSubmit)

  const handleEditAgent = agent => {
    startTransition(() => {
      setEditingAgent(agent);
      // Form data is handled by AgentForm component
      setShowCreateModal(true);
    });
  };

  // (Update handled by AgentForm via handleSubmit)

  const handleDeleteAgent = async agentId => {
    const ok = await confirm({
      title: 'Delete agent?',
      description: 'Are you sure you want to delete this agent? This cannot be undone.',
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;

    try {
      await dispatch(deleteAgent(agentId)).unwrap();
      toast.success('Agent deleted successfully');
    } catch (error) {
      toast.error(`Failed to delete agent: ${error.message}`);
    }
  };

  const handleSelectAgent = async agent => {
    dispatch(selectAgent(agent));
    toast.success(`Selected agent: ${agent.name}`);

    // Save default agent ID to user preferences
    try {
      const agentId = agent._id || agent.id;
      if (agentId) {
        await dispatch(saveDefaultAgent(agentId));
      }
    } catch (error) {
      console.error('Failed to save default agent preference:', error);
      // Don't show error toast - this is a background operation
    }
  };

  // Wrapper function for AgentForm
  const handleSubmit = async agentData => {
    try {
      if (editingAgent) {
        const agentId = editingAgent.id || editingAgent._id;
        if (!agentId) {
          toast.error('Agent ID not found. Please try again.');
          return;
        }
        await dispatch(updateAgent({ agentId, agentData })).unwrap();
        toast.success('Agent updated successfully');
      } else {
        const newAgent = await dispatch(createAgent(agentData)).unwrap();
        toast.success('Agent created successfully');
        // Save newly created agent as default
        const agentId = newAgent._id || newAgent.id;
        if (agentId) {
          try {
            await dispatch(saveDefaultAgent(agentId));
          } catch (error) {
            console.error('Failed to save default agent preference:', error);
            // Don't show error - this is a background operation
          }
        }
      }
      setShowCreateModal(false);
      setEditingAgent(null);
    } catch (error) {
      toast.error(error.message || 'Failed to save agent');
    }
  };

  // Test agent function
  const handleTestAgent = agent => {
    // Persist for cross-route prefill
    const agentTestData = {
      name: agent.name,
      persona: agent.persona,
      aiProvider: agent.aiProvider,
      aiModel: agent.aiModel,
      aiApiKey: agent.aiApiKey || '',
      voiceProvider: agent.voiceProvider,
      voiceId: agent.voiceId,
      voiceApiKey: agent.voiceApiKey || '',
      sttProvider: agent.sttProvider,
      sttLanguage: agent.sttLanguage,
      sttApiKey: agent.sttApiKey || '',
    };

    sessionStorage.setItem('testAgentData', JSON.stringify(agentTestData));
    dispatch(selectAgent(agent));
    navigate('/testing');
    toast.success(`Testing agent: ${agent.name} → Testing`);
  };

  // Call Me function
  const handleCallMe = async (agent, e) => {
    e.stopPropagation();

    if (!isPhoneVerified) {
      toast.error('Please verify your phone number first. Go to Call Me page to verify.');
      navigate('/call-me');
      return;
    }

    const agentId = agent.id || agent._id;
    if (!agentId) {
      toast.error('Agent ID not found');
      return;
    }

    try {
      setCallingAgentId(agentId);
      const response = await callMeService.startCall(agentId);

      if (response.success) {
        toast.success(
          `Call initiated! You will receive a call on ${user?.profile?.phone} shortly.`
        );
      } else {
        toast.error(response.message || response.error || 'Failed to initiate call');
      }
    } catch (error) {
      console.error('Failed to start call:', error);
      const errorMessage = error.message || 'Failed to initiate call';

      // If phone not verified or feature disabled, redirect to Call Me page
      if (errorMessage.includes('not verified') || errorMessage.includes('not enabled')) {
        toast.error(errorMessage);
        navigate('/call-me');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setCallingAgentId(null);
    }
  };

  const openCreateFlow = () => {
    if (editingAgent) {
      setShowCreateModal(true);
      return;
    }
    if (workspace === 'builder' || workspace === 'enterprise') {
      setShowCreateChooser(true);
    } else {
      setShowSimpleWizard(true);
    }
  };

  const getCallMeButtonState = agent => {
    if (!isCallMeEnabled) {
      return {
        disabled: true,
        tooltip: 'Call Me feature is not enabled',
      };
    }
    if (!isPhoneVerified) {
      return {
        disabled: true,
        tooltip: 'Please verify your phone number first',
      };
    }
    if (callingAgentId === (agent.id || agent._id)) {
      return {
        disabled: true,
        tooltip: 'Calling...',
      };
    }
    return {
      disabled: false,
      tooltip: 'Call Me - Test this agent via phone call',
    };
  };

  return (
    <>
      <div className="space-y-6" data-testid="agents-page">
        <HelpBanner
          title="New to voice agents?"
          description="Start with a simple agent, then add knowledge and connect your phone. Advanced STT/LLM/TTS settings stay in the agent editor."
          docHref={helpLinks.businessQuickstart}
        />
        {!canCreateAgent && <UpgradePromptCard className="mb-2" />}
        {isAgency && activeClient && (
          <p className="text-sm text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg px-3 py-2">
            Viewing client: <strong>{activeClient.name}</strong> (label only — assign agents by name prefix
            for now)
          </p>
        )}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Agents</h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Create and manage your AI voice agents with custom personas and configurations
            </p>
            {selectedAgent && (
              <div className="mt-2 p-2 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <span className="text-sm text-green-700 dark:text-green-300">
                  <strong>Selected Agent:</strong> {selectedAgent.name}
                </span>
              </div>
            )}
          </div>
          <div className="flex-shrink-0">
            <button
              type="button"
              data-testid="agents-create-btn"
              onClick={openCreateFlow}
              disabled={!canCreateAgent}
              className={`btn-primary-gradient flex items-center space-x-2 ${!canCreateAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={
                !canCreateAgent
                  ? 'Agent limit reached. Ask an admin to increase your agent allocation, or upgrade your plan.'
                  : ''
              }
            >
              <Plus className="h-4 w-4" />
              <span>Create Agent</span>
            </button>
          </div>
        </div>

        {/* Agents Grid */}
        {agents.length === 0 ? (
          <div className="card-user text-center py-12 px-6">
            <Bot className="h-16 w-16 mx-auto mb-4 text-gray-400 dark:text-gray-500" />
            <Heading level={3} weight="medium" className="mb-2">
              No agents yet
            </Heading>
            <Text color="muted" className="mb-4">
              Create your first AI agent to get started with voice conversations
            </Text>
            <button
              onClick={openCreateFlow}
              disabled={!canCreateAgent}
              className={`btn-primary-gradient ${!canCreateAgent ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={
                !canCreateAgent
                  ? 'Agent limit reached. Ask an admin to increase your agent allocation, or upgrade your plan.'
                  : ''
              }
            >
              Create Your First Agent
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map(agent => {
              const agentId = String(agent.id || agent._id || '');
              const selectedId = String(selectedAgent?.id || selectedAgent?._id || '');
              const isSelected = selectedId !== '' && selectedId === agentId;
              return (
                <div
                  key={agentId}
                  className={`card-user p-5 sm:p-6 hover:shadow-lg transition-shadow cursor-pointer ${
                    isSelected
                      ? 'ring-2 ring-[hsl(var(--brand-green))] bg-green-50 dark:bg-green-900/20'
                      : ''
                  }`}
                  onClick={() => handleSelectAgent(agent)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                        <Bot className="h-6 w-6 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Heading level={3} weight="semibold">
                            {agent.name}
                          </Heading>
                          {agent.isRootAgent && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                              Demo Agent
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              agent.isActive
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                            }`}
                          >
                            {agent.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {/* Stylish Radio Button */}
                    <div className="flex items-center">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSelected
                            ? 'border-[hsl(var(--brand-green))] bg-[hsl(var(--brand-green))]'
                            : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700'
                        }`}
                      >
                        {isSelected && (
                          <div className="w-3 h-3 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <Text size="sm" color="muted">
                        AI Provider:
                      </Text>
                      <Text size="sm" weight="medium" className="capitalize">
                        {agent.aiProvider}
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text size="sm" color="muted">
                        AI Model:
                      </Text>
                      <Text size="sm" weight="medium">
                        {agent.aiModel}
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text size="sm" color="muted">
                        Voice Provider:
                      </Text>
                      <Text size="sm" weight="medium" className="capitalize">
                        {agent.voiceProvider}
                      </Text>
                    </div>
                    <div className="flex justify-between">
                      <Text size="sm" color="muted">
                        Voice ID:
                      </Text>
                      <Text size="sm" weight="medium">
                        {agent.voiceId}
                      </Text>
                    </div>
                  </div>

                  <Text size="sm" color="muted" className="mb-4 line-clamp-2">
                    {agent.persona}
                  </Text>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleEditAgent(agent);
                      }}
                      disabled={agent.isRootAgent && user?.role !== 'admin'}
                      title={
                        agent.isRootAgent && user?.role !== 'admin'
                          ? 'Root agents can only be edited by administrators'
                          : 'Edit agent'
                      }
                      className={`flex-1 btn-secondary-glass flex items-center justify-center space-x-1 min-w-[80px] ${
                        agent.isRootAgent && user?.role !== 'admin'
                          ? 'opacity-50 cursor-not-allowed'
                          : ''
                      }`}
                    >
                      <Edit className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setEmbedAgent(agent);
                      }}
                      className="flex-1 btn-secondary-glass flex items-center justify-center space-x-1 min-w-[80px]"
                      title="Add chat widget to your website"
                    >
                      <Code2 className="h-4 w-4" />
                      <span>Embed</span>
                    </button>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleTestAgent(agent);
                      }}
                      className="flex-1 btn-primary-gradient flex items-center justify-center space-x-1 min-w-[80px]"
                    >
                      <TestTube className="h-4 w-4" />
                      <span>Test</span>
                    </button>
                    {isCallMeEnabled && (
                      <button
                        onClick={e => handleCallMe(agent, e)}
                        disabled={getCallMeButtonState(agent).disabled}
                        title={getCallMeButtonState(agent).tooltip}
                        className={`flex-1 flex items-center justify-center space-x-1 min-w-[80px] ${
                          getCallMeButtonState(agent).disabled
                            ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        } px-3 py-2 rounded-md text-sm font-medium transition-colors`}
                      >
                        {callingAgentId === (agent.id || agent._id) ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Calling...</span>
                          </>
                        ) : (
                          <>
                            <PhoneIncoming className="h-4 w-4" />
                            <span>Call Me</span>
                          </>
                        )}
                      </button>
                    )}
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleDeleteAgent(agentId);
                      }}
                      disabled={agent.isRootAgent && user?.role !== 'admin'}
                      title={
                        agent.isRootAgent && user?.role !== 'admin'
                          ? 'Root agents can only be deleted by administrators'
                          : 'Delete agent'
                      }
                      className={`btn-danger p-2 ${agent.isRootAgent && user?.role !== 'admin' ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AgentCreateChooser
        isOpen={showCreateChooser}
        onClose={() => setShowCreateChooser(false)}
        onQuickSetup={() => setShowSimpleWizard(true)}
        onAdvanced={() => setShowCreateModal(true)}
      />
      <AgentSimpleWizard
        isOpen={showSimpleWizard}
        onClose={() => setShowSimpleWizard(false)}
        onSubmit={handleSubmit}
        onOpenAdvanced={() => {
          setShowSimpleWizard(false);
          setShowCreateModal(true);
        }}
      />
      <AgentForm
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setEditingAgent(null);
        }}
        onSubmit={handleSubmit}
        editingAgent={editingAgent}
        title={editingAgent ? 'Edit Agent' : 'Create New Agent'}
      />

      <Dialog open={!!embedAgent} onOpenChange={(open) => !open && setEmbedAgent(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Embed on your website — {embedAgent?.name || 'Agent'}
            </DialogTitle>
          </DialogHeader>
          <AgentEmbedPanel
            agentId={embedAgent?.id || embedAgent?._id}
            onClose={() => setEmbedAgent(null)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Agents;
